import cls from 'classnames';
import React, { Component } from 'react';
import FlipMove from 'react-flip-move';
import Measure from 'react-measure';
import { RicosContext } from 'ricos-context';
import {
  PollData_Layout_PollLayout_Type,
  PollData_Poll_Settings_Permissions_ViewRole,
} from 'ricos-schema';

import { AddIcon, LoaderIcon } from '../assets/icons';
import styles from '../components/poll.scss';
import { PollContextPropTypes, withPoll } from '../components/poll-context';
import { PollHeader } from '../components/poll-header';
import { PollOption } from '../components/poll-option';
import { RCEHelpersPropTypes, withRCEHelpers } from '../components/rce-helpers-context';
import { VotedUsers } from '../components/voted-users';
import {
  POLLS_ADD_ANSWER_BUTTON,
  POLLS_ANSWER_ITEMS,
  POLLS_MULTIPLE_ANSWER_TEXT,
  ROOT,
} from '../dataHooks';
import { toBackgroundString } from '../helpers';

interface Props {
  poll: {
    creatorFlag: boolean;
    ownVotes: string[];
    id: string;
    title: string;
    settings: {
      permissions: {
        view: string;
        vote: string;
        allowMultipleVotes: boolean;
      };
      showVoters: boolean;
      showVotesCount: boolean;
    };
    options: any[];
  };
  rce: {
    isWebView: boolean;
    preventInteraction: any;
    isPreview: boolean;
    isViewMode: boolean;
    setInPluginEditingMode: (boolean) => void;
    helpers: {
      handleFileUpload: () => void;
    };
    getSiteMembers: () => void;
  };
  fetchPoll: () => void;
  vote: (any) => void;
  unvote: (any) => void;
  updatePollOption: (number, any) => void;
  removeOption: (any) => void;
  layout: any;
  design: any;
  getVoters;
  siteMembers;
  addOption;
}

interface State {
  collapsed: boolean;
  error: any;
  loading: boolean;
  bounds: any;
}

class PollComponent extends Component<Props, State> {
  static propTypes = {
    ...PollContextPropTypes,
    ...RCEHelpersPropTypes,
  };

  constructor(props) {
    super(props);
    this.state = {
      collapsed: this.isInitiallyCollapsed(),
      error: null,
      loading: false,
      bounds: null,
    };
  }

  static contextType = RicosContext;

  declare context: React.ContextType<typeof RicosContext>;

  async componentDidMount() {
    const { poll } = this.props;

    if (poll.id) {
      this.fetchPoll();
    }
  }

  componentDidCatch(error) {
    console.error(error);
    this.setState({ error: error.message });
  }

  async fetchPoll() {
    const { t } = this.context;
    if (this.props.rce.isWebView || this.props.rce.preventInteraction) {
      return;
    }
    this.setState({
      loading: true,
      error: null,
    });

    try {
      await this.props.fetchPoll();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[rce-social-polls] fetchPoll: ', error);
      this.setState({ error: t('Poll_Viewer_Toast_Error_ServerDown') });
    } finally {
      this.setState({ loading: false });
    }
  }

  vote = async optionId => {
    this.setState({ error: null });
    try {
      await this.props.vote(optionId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[rce-social-polls] vote: ', error.response);
      this.setState({ error: error.message });

      return Promise.reject(error);
    }
  };

  unvote = async optionId => {
    this.setState({ error: null });
    try {
      await this.props.unvote(optionId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[rce-social-polls] unvote: ', error.response);
      this.setState({ error: error.message });

      return Promise.reject(error);
    }
  };

  isInitiallyCollapsed() {
    const { poll } = this.props;

    return poll.options.length > 4;
  }

  toggleCollapse = () => this.setState({ collapsed: !this.state.collapsed });

  handleOptionUpdate(index) {
    return option => this.props.updatePollOption(index, option);
  }

  handleOptionRemove(index) {
    return () => this.props.removeOption(index);
  }

  getNotes() {
    const { poll } = this.props;
    const { t } = this.context;

    return [
      {
        condition: poll.settings.permissions?.allowMultipleVotes,
        label: t('Poll_Viewer_Multiselect_Footer'),
      },
      {
        condition:
          poll.settings.permissions?.view === PollData_Poll_Settings_Permissions_ViewRole.CREATOR &&
          poll.creatorFlag,
        label: t('Poll_Viewer_Visibility_Owner_Footer'),
      },
    ].filter(note => !!note.condition);
  }

  showResults() {
    const { permissions } = this.props.poll.settings;

    switch (permissions?.view) {
      case PollData_Poll_Settings_Permissions_ViewRole.EVERYONE:
        return true;

      case PollData_Poll_Settings_Permissions_ViewRole.VOTERS:
        return this.props.poll.creatorFlag || !!this.props.poll.ownVotes?.length;

      case PollData_Poll_Settings_Permissions_ViewRole.CREATOR:
        return this.props.poll.creatorFlag;

      default:
        return false;
    }
  }

  hasImageBackground() {
    return !!this.props.design.poll?.background?.image?.src?.url;
  }

  getOptionList() {
    const { poll, rce } = this.props;
    const { collapsed } = this.state;

    if (!rce.isViewMode || this.showEditControls()) {
      return poll.options;
    }

    let list;

    if (!this.showResults()) {
      list = poll.options;
    } else {
      list = Array.from(poll.options).sort((prev, option) => {
        if (!prev.rating) {
          return 1;
        }

        if (option.rating === prev.rating) {
          return 0;
        }

        if (option.rating > prev.rating) {
          return 1;
        }

        return -1;
      });
    }

    return collapsed ? list.slice(0, 4) : list;
  }

  handleCTAFocus = e => {
    e.stopPropagation();
    this.props.rce.setInPluginEditingMode(true);
  };

  handleCTABlur = () => this.props.rce.setInPluginEditingMode(false);

  onResize = ({ bounds }) => this.setState({ bounds });

  showEditControls() {
    const { rce } = this.props;

    return !rce.isViewMode && !rce.isPreview;
  }

  render() {
    const { poll, rce, getVoters, addOption, design, layout, siteMembers } = this.props;
    const { collapsed, loading, bounds } = this.state;
    const { isMobile, t } = this.context;
    const style = {
      ...design.poll,
      background: toBackgroundString(
        design.poll?.background,
        design.poll?.background.type,
        bounds?.width,
        bounds?.height
      ),
    };

    return (
      <Measure bounds onResize={this.onResize}>
        {({ measureRef }) => (
          <div
            className={cls(styles.container, {
              [styles.isMobile]: isMobile,
              [styles.webview]: rce.isWebView,
              [styles.fullscreen]: rce.isPreview,
              [styles.dark]: this.hasImageBackground(),
            })}
            style={style}
            dir={layout.poll?.direction}
            ref={measureRef}
            data-hook={ROOT}
          >
            <div
              className={cls(styles.background_overlay, {
                [styles.with_image]: this.hasImageBackground(),
              })}
            />
            <PollHeader />

            <ul
              className={cls(styles.options, {
                [styles.list]: layout.poll?.type === PollData_Layout_PollLayout_Type.LIST,
                [styles.grid]: layout.poll?.type === PollData_Layout_PollLayout_Type.GRID,
                [styles.with_image]: layout.options?.enableImage,
              })}
            >
              <FlipMove
                typeName={null}
                disableAllAnimations={this.showEditControls()}
                enterAnimation="none"
                leaveAnimation="none"
                verticalAlignment="bottom"
              >
                {this.getOptionList().map((option, i) => (
                  <li className={styles.option} key={option.id || i} data-hook={POLLS_ANSWER_ITEMS}>
                    <PollOption
                      option={option}
                      update={this.handleOptionUpdate(i)}
                      remove={this.handleOptionRemove(i)}
                      removeEnabled={this.showEditControls() && poll.options.length > 1}
                      vote={this.vote}
                      unvote={this.unvote}
                      poll={poll}
                      showResults={this.showResults()}
                      dark={this.hasImageBackground()}
                    />
                    <VotedUsers
                      option={option}
                      siteMembers={siteMembers}
                      showResults={this.showResults()}
                      showVoters={poll.settings.showVoters}
                      showVotes={poll.settings.showVotesCount}
                      fetchVoters={params => getVoters(option.id, params)}
                    />
                  </li>
                ))}

                {this.showEditControls() && (
                  <li className={styles.column}>
                    <button
                      onClick={addOption}
                      onFocus={this.handleCTAFocus}
                      onBlur={this.handleCTABlur}
                      className={styles.add_option}
                      style={design.options}
                      type="button"
                      data-hook={POLLS_ADD_ANSWER_BUTTON}
                    >
                      {layout.poll?.type === PollData_Layout_PollLayout_Type.GRID &&
                      layout.options?.enableImage ? (
                        <AddIcon />
                      ) : (
                        <>
                          <AddIcon width={24} height={24} />
                          &nbsp;
                          {t('Poll_Editor_Answer_AddAnswer')}
                        </>
                      )}
                    </button>
                  </li>
                )}
              </FlipMove>
            </ul>

            {poll.options.length > 4 && !this.showEditControls() && (
              <button
                onClick={this.toggleCollapse}
                className={styles.see_more}
                style={design.options}
                type="button"
              >
                {collapsed
                  ? this.showResults()
                    ? t('Poll_Viewer_ShowAllResults_CTA')
                    : t('Poll_Viewer_ShowAllOptions_CTA')
                  : t('Poll_Viewer_ShowLess_CTA')}
              </button>
            )}

            {this.getNotes().map((note, i) => (
              <p className={styles.additional_note} key={i} data-hook={POLLS_MULTIPLE_ANSWER_TEXT}>
                {note.label}
              </p>
            ))}

            <LoaderIcon
              className={cls(styles.spinner, {
                [styles.shown]: loading,
              })}
              width={24}
              height={24}
            />
          </div>
        )}
      </Measure>
    );
  }
}

export const Poll = withRCEHelpers(withPoll(PollComponent));
